/*
 * Original Canvas2D atmosphere. The photographic island and beach are never
 * painted over: everything uses the photograph's centred cover transform.
 * coast:time { phase: 0..1, night: 0..1, mode } drives the celestial clock.
 * coast:pause / coast:motion share the existing scenery motion ownership.
 */
(() => {
  'use strict';
  if (window.__cgauCoastalSkyRuntime) return;

  const SOURCE_W = 1672;
  const SOURCE_H = 941;
  const SUN_MIN_RADIUS = 37;
  const SUN_MAX_RADIUS = 42;
  const MOON_RADIUS = 43;
  const CLEARANCE_RADIUS = Math.max(SUN_MAX_RADIUS, MOON_RADIUS);
  const TAU = Math.PI * 2;
  const clamp = (n, min = 0, max = 1) => Math.max(min, Math.min(max, n));
  const numeric = (n, fallback) => n !== '' && n != null && Number.isFinite(Number(n)) ? Number(n) : fallback;
  const smooth = (lo, hi, n) => { const v = clamp((n - lo) / (hi - lo)); return v * v * (3 - 2 * v); };
  const mix = (a, b, n) => a + (b - a) * n;
  const hash = n => { const v = Math.sin(n * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };

  // Lower than the actual silhouette by at most a few pixels, avoiding bright
  // celestial edges being drawn on the ridge. Coordinates are photo-relative.
  const RIDGE = [
    [0, .501], [.09, .501], [.106, .487], [.128, .480], [.152, .474],
    [.167, .481], [.194, .481], [.216, .475], [.24, .472], [.264, .460],
    [.282, .463], [.299, .458], [.32, .466], [.351, .450], [.377, .441],
    [.40, .438], [.423, .437], [.45, .421], [.466, .416], [.478, .420],
    [.49, .415], [.505, .416], [.525, .430], [.551, .439], [.579, .447],
    [.604, .459], [.631, .463], [.651, .461], [.677, .451], [.70, .449],
    [.724, .463], [.751, .473], [.778, .477], [.797, .481], [.828, .481],
    [.85, .480], [.868, .475], [.893, .480], [.923, .486], [.945, .499],
    [.965, .502], [1, .502]
  ];

  const skyline = x => {
    for (let i = 1; i < RIDGE.length; i++) {
      if (x <= RIDGE[i][0]) {
        const left = RIDGE[i - 1], right = RIDGE[i];
        return mix(left[1], right[1], (x - left[0]) / (right[0] - left[0]));
      }
    }
    return .502;
  };

  function moonTexture() {
    const tile = document.createElement('canvas');
    tile.width = tile.height = 192;
    const ctx = tile.getContext('2d');
    if (!ctx) return tile;
    const pixels = ctx.createImageData(192, 192);
    const craters = Array.from({length: 38}, (_, i) => ({
      x: hash(i + 17) * 1.64 - .82,
      y: hash(i + 73) * 1.64 - .82,
      r: .018 + hash(i + 157) * .13,
      d: .06 + hash(i + 317) * .13
    }));
    for (let py = 0; py < 192; py++) {
      for (let px = 0; px < 192; px++) {
        const x = (px - 95.5) / 95, y = (py - 95.5) / 95;
        const radius = Math.hypot(x, y);
        if (radius >= 1) continue;
        const z = Math.sqrt(1 - radius * radius);
        let shade = .48 + .50 * clamp(z * .87 - x * .2 - y * .15);
        const maria = Math.sin(x * 8.4 + Math.cos(y * 7.3)) * Math.cos(y * 6.1 - x * 2.7)
          + Math.sin(x * 14.7 + y * 10.3) * .35;
        shade -= smooth(.05, 1.15, maria) * .22;
        shade += (hash(px + py * 193) - .5) * .065;
        for (const crater of craters) {
          const distance = Math.hypot(x - crater.x, y - crater.y) / crater.r;
          if (distance < 1.3) {
            shade -= (1 - smooth(.15, .85, distance)) * crater.d;
            shade += Math.exp(-Math.pow((distance - .96) * 9, 2)) * .075;
          }
        }
        const value = clamp(shade * 1.15) * 252, at = (py * 192 + px) * 4;
        pixels.data[at] = value;
        pixels.data[at + 1] = value * .986;
        pixels.data[at + 2] = value * .945;
        pixels.data[at + 3] = clamp((1 - radius) * 80) * 255;
      }
    }
    ctx.putImageData(pixels, 0, 0);
    return tile;
  }

  function cloudTexture() {
    const tile = document.createElement('canvas');
    tile.width = 680;
    tile.height = 160;
    const ctx = tile.getContext('2d');
    if (!ctx) return tile;
    // Layered, slender wisps. A cached texture keeps gradients off the hot path.
    for (let i = 0; i < 60; i++) {
      const x = 80 + hash(i + 822) * 525;
      const y = 51 + hash(i + 347) * 52;
      const w = 50 + hash(i + 631) * 100;
      const h = 3 + hash(i + 413) * 9;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-.095);
      ctx.scale(w, h);
      const gradient = ctx.createRadialGradient(0, 0, .02, 0, 0, 1);
      gradient.addColorStop(0, 'rgba(255,245,224,.10)');
      gradient.addColorStop(.45, 'rgba(255,245,224,.05)');
      gradient.addColorStop(1, 'rgba(255,245,224,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(-1, -1, 2, 2);
      ctx.restore();
    }
    return tile;
  }

  class CoastalSky {
    constructor(scene) {
      if (scene.querySelector('[data-coast-sky]')) return;
      this.scene = scene;
      this.canvas = document.createElement('canvas');
      this.canvas.dataset.coastSky = '';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.canvas.setAttribute('role', 'presentation');
      scene.append(this.canvas);
      this.ctx = this.canvas.getContext('2d', {alpha: true});
      if (!this.ctx) { this.canvas.remove(); scene.dataset.skyRenderer = 'unavailable'; return; }
      this.moon = moonTexture();
      this.cloud = cloudTexture();
      this.listeners = [];
      this.frame = 0;
      this.frames = 0;
      this.lastStamp = 0;
      this.lastDraw = 0;
      this.elapsed = 0;
      this.destroyed = false;
      this.suspended = false;
      const section = scene.closest('[data-coastal-section]');
      this.userPaused = section?.dataset.paused === 'true';
      this.ambientPaused = section?.dataset.cgauMotionPaused === 'true';
      this.pendingStill = true;
      this.pendingResize = false;
      this.motionOverride = false;
      this.rendererBlocked = ['paused', 'ambient-paused', 'hidden', 'reduced', 'offscreen'].includes(scene.dataset.motion);
      this.preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.rendererSuspended = ['ambient-paused', 'hidden', 'offscreen'].includes(scene.dataset.motion);
      this.card = section?.querySelector('.newsletter-card') || null;
      this.mode = scene.dataset.mode || section?.dataset.mode || 'day';
      this.phase = clamp(numeric(scene.dataset.phase, this.mode === 'night' ? .74 : this.mode === 'dusk' ? .46 : .25));
      this.night = clamp(numeric(scene.dataset.night, this.mode === 'night' ? 1 : 0));
      this.warm = clamp(numeric(scene.dataset.warm, 0));
      this.twilight = clamp(numeric(scene.dataset.twilight, 0));
      this.lastRenderer = scene.dataset.renderer || '';
      this.stars = Array.from({length: 116}, (_, i) => ({
        x: hash(i + 1041), y: .02 + hash(i + 775) * .43,
        radius: .36 + Math.pow(hash(i + 108), 4) * .8,
        phase: hash(i + 384) * TAU, speed: .3 + hash(i + 429) * .5
      }));
      this.glints = Array.from({length: 240}, (_, i) => ({
        depth: hash(i + 2491), spread: (hash(i + 2011) - .5) * 2,
        width: .2 + hash(i + 329), phase: hash(i + 97) * TAU,
        frequency: .5 + hash(i + 577) * 1.4
      }));
      this.tick = this.tick.bind(this);
      scene.dataset.skyRenderer = 'canvas2d';
      const box = scene.getBoundingClientRect();
      this.visible = box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth;
      this.listen(scene, 'coast:time', event => {
        const nextPhase = clamp(numeric(event.detail?.phase, this.phase));
        const nextNight = clamp(numeric(event.detail?.night, this.night));
        const nextWarm = clamp(numeric(event.detail?.warm, this.warm));
        const nextTwilight = clamp(numeric(event.detail?.twilight, this.twilight));
        const nextMode = event.detail?.mode || this.mode;
        const modeChanged = nextMode !== this.mode;
        const changed = nextPhase !== this.phase || nextNight !== this.night || nextWarm !== this.warm || nextTwilight !== this.twilight || modeChanged;
        this.phase = nextPhase;
        this.night = nextNight;
        this.warm = nextWarm; this.twilight = nextTwilight;
        this.mode = nextMode;
        if (changed) this.pendingStill = true;
        if ((modeChanged || event.detail?.still === true) && !this.canAnimate()) this.draw();
      });
      this.listen(scene, 'coast:motion', event => {
        const state = event.detail?.state;
        const renderer = this.scene.dataset.renderer || '';
        if (renderer !== this.lastRenderer) { this.lastRenderer = renderer; this.pendingStill = true; }
        // A failed WebGL renderer does not disable the independent Canvas2D sky.
        this.rendererBlocked = ['paused', 'ambient-paused', 'hidden', 'reduced', 'offscreen'].includes(state);
        this.rendererSuspended = ['ambient-paused', 'hidden', 'offscreen'].includes(state);
        this.updateMotion();
      });
      this.listen(scene, 'coast:pause', event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.userPaused = event.detail.paused;
        if (this.userPaused) this.motionOverride = false;
        else if (event.detail.userInitiated === true) this.motionOverride = true;
        this.updateMotion();
      });
      const ambient = event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.ambientPaused = event.detail.paused;
        this.updateMotion();
      };
      this.listen(scene, 'cgau:ambient-motion-change', ambient);
      if (section) this.listen(section, 'cgau:ambient-motion-change', ambient);
      this.listen(window, 'cgau:ambient-motion-change', ambient);
      this.listen(document, 'visibilitychange', () => this.updateMotion());
      this.listen(this.preference, 'change', () => {
        this.motionOverride = false;
        this.pendingStill = true;
        this.updateMotion();
      });
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.updateMotion();
        }, {threshold: 0});
        this.intersectionObserver.observe(scene);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(scene);
        if (this.card) {
          this.resizeObserver.observe(this.card);
          if (this.card.parentElement) this.resizeObserver.observe(this.card.parentElement);
        }
      } else this.listen(window, 'resize', () => this.resize());
      this.resize();
      this.updateMotion();
    }

    listen(target, name, callback) {
      target.addEventListener(name, callback);
      this.listeners.push(() => target.removeEventListener(name, callback));
    }

    drawable() {
      return !this.destroyed && !this.suspended && !document.hidden && this.visible
        && !this.ambientPaused && !this.rendererSuspended && this.width > 0 && this.height > 0;
    }

    canAnimate() {
      return this.drawable() && !this.userPaused && !this.rendererBlocked
        && (!this.preference.matches || this.motionOverride);
    }

    updateMotion() {
      if (this.destroyed) return;
      if (this.canAnimate()) {
        this.scene.dataset.skyMotion = 'running';
        if (!this.frame) { this.lastStamp = 0; this.frame = requestAnimationFrame(this.tick); }
      } else {
        this.stop();
        this.scene.dataset.skyMotion = document.hidden || this.suspended ? 'hidden' : !this.visible ? 'offscreen'
          : this.ambientPaused || this.rendererSuspended ? 'ambient-paused'
            : this.preference.matches && !this.motionOverride ? 'reduced' : 'paused';
      }
      if (this.pendingStill && this.drawable()) this.draw();
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastStamp = 0;
    }

    tick(stamp) {
      this.frame = 0;
      if (!this.canAnimate()) { this.updateMotion(); return; }
      if (this.lastStamp) this.elapsed += Math.min(.1, (stamp - this.lastStamp) / 1000);
      this.lastStamp = stamp;
      if (stamp - this.lastDraw >= 1000 / 30 - .4) {
        this.lastDraw = stamp;
        this.draw();
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    resize() {
      if (this.destroyed) return;
      const {width, height, top, left} = this.scene.getBoundingClientRect();
      if (!width || !height) {
        this.width = width; this.height = height;
        this.pendingResize = true; this.pendingStill = true; this.updateMotion();
        return;
      }
      this.width = width;
      this.height = height;
      const dpr = Math.min(1.5, window.devicePixelRatio || 1, Math.sqrt(1700000 / (width * height)));
      this.pixelWidth = Math.max(1, Math.round(width * dpr));
      this.pixelHeight = Math.max(1, Math.round(height * dpr));
      this.dpr = this.pixelWidth / width;
      this.pendingResize = true;
      this.scale = Math.max(width / SOURCE_W, height / SOURCE_H);
      this.offsetX = (width - SOURCE_W * this.scale) / 2;
      this.offsetY = (height - SOURCE_H * this.scale) / 2;
      // On a phone the only open sky is below the newsletter. On desktop,
      // measured side gutters let the enlarged bodies climb high beside it.
      const minimumPeak = width < 700
        ? Math.max(.06, (150 + CLEARANCE_RADIUS * this.scale + 6 - this.offsetY) / (SOURCE_H * this.scale))
        : .06;
      const cardBounds = this.card?.getBoundingClientRect();
      let belowCardPeak = minimumPeak;
      if (cardBounds?.height > 0) {
        const cardBottom = cardBounds.bottom - top;
        const requiredPeak = (cardBottom + CLEARANCE_RADIUS * this.scale + 8 - this.offsetY) / (SOURCE_H * this.scale);
        belowCardPeak = clamp(Math.max(minimumPeak, requiredPeak), .06, .51);
        this.scene.dataset.skyCardBottom = cardBottom.toFixed(1);
        this.scene.dataset.skyArcClearance = (skyline(.5) * SOURCE_H * this.scale + this.offsetY
          - cardBottom - CLEARANCE_RADIUS * 2 * this.scale).toFixed(1);
      }
      const makeOrbit = (body, extent) => {
        const fallback = {left: width * .06, right: width * .94, peak: belowCardPeak, mode: 'below-card'};
        if (width < 700 || !Number.isFinite(cardBounds?.left) || !Number.isFinite(cardBounds?.right)) return fallback;
        const gutterLeft = body === 'sun' ? 0 : cardBounds.right - left;
        const gutterRight = body === 'sun' ? cardBounds.left - left : width;
        const margin = extent * this.scale + 8;
        if (gutterRight - gutterLeft < margin * 2 + 24) return fallback;
        const peakY = Math.max(height * .18, margin + 8);
        return {left: gutterLeft + margin, right: gutterRight - margin,
          peak: (peakY - this.offsetY) / (SOURCE_H * this.scale), mode: body === 'sun' ? 'left-gutter' : 'right-gutter'};
      };
      // All twelve rays clear the card. The moon reserves its bright inner halo;
      // the outer atmospheric bloom may gently disappear behind the card.
      this.sunOrbit = makeOrbit('sun', SUN_MAX_RADIUS * 2.14);
      this.moonOrbit = makeOrbit('moon', MOON_RADIUS * 1.35);
      this.arcPeak = this.sunOrbit.peak;
      this.scene.dataset.skyTravelShift = '0.000';
      this.scene.dataset.skySunOrbit = this.sunOrbit.mode;
      this.scene.dataset.skyMoonOrbit = this.moonOrbit.mode;
      this.scene.dataset.skySunArcPeak = this.sunOrbit.peak.toFixed(4);
      this.scene.dataset.skyMoonArcPeak = this.moonOrbit.peak.toFixed(4);
      this.scene.dataset.skyArcPeak = this.arcPeak.toFixed(4);
      this.pendingStill = true;
      this.updateMotion();
    }

    skyClip() {
      const ctx = this.ctx;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(SOURCE_W, 0);
      for (let i = RIDGE.length - 1; i >= 0; i--) ctx.lineTo(RIDGE[i][0] * SOURCE_W, RIDGE[i][1] * SOURCE_H);
      ctx.closePath();
      ctx.clip();
    }

    position(progress, orbit) {
      const progress01 = clamp(progress);
      const x = mix(orbit.left, orbit.right, progress01);
      return {
        x: (x - this.offsetX) / (this.scale * SOURCE_W),
        // Both ends are fully below the ridge before switching celestial body.
        y: .56 - (.56 - orbit.peak) * Math.sin(Math.PI * progress01)
      };
    }

    draw() {
      if (!this.drawable()) { this.pendingStill = true; return; }
      if (this.pendingResize) {
        this.canvas.width = this.pixelWidth; this.canvas.height = this.pixelHeight;
        this.pendingResize = false;
      }
      const ctx = this.ctx, phase = this.phase, time = this.elapsed;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      ctx.setTransform(this.dpr * this.scale, 0, 0, this.dpr * this.scale, this.offsetX * this.dpr, this.offsetY * this.dpr);
      const sun = this.position(phase * 2, this.sunOrbit), moon = this.position((phase - .5) * 2, this.moonOrbit);
      const isNight = phase >= .5;
      const body = isNight ? moon : sun;
      const bodyRadius = isNight ? MOON_RADIUS : mix(SUN_MIN_RADIUS, SUN_MAX_RADIUS, smooth(.22, .5, sun.y));
      // The enlarged upper limb must emerge before its centre clears the ridge.
      // Actual occultation still comes from the unchanged photographic mask.
      const limb = bodyRadius / SOURCE_H;
      const visibleBody = smooth(-limb - .002, -limb + .004, skyline(body.x) - body.y);
      const visibility = visibleBody * (isNight ? smooth(.2, .8, this.night) : 1 - smooth(.8, 1, this.night));
      const skyNight = smooth(.28, .94, this.night);
      ctx.save();
      this.skyClip();
      this.drawFallbackGrade();
      this.drawStars(skyNight, time);
      if (isNight) this.drawMoon(moon, visibility);
      else this.drawSun(sun, visibility);
      this.drawClouds(time, skyNight);
      this.drawMeteor(skyNight, phase);
      ctx.restore();
      // Water reflections are shaded by the V3 surface renderer.
      const data = this.scene.dataset;
      data.skyPhase = phase.toFixed(5);
      data.sunX = sun.x.toFixed(4);
      data.sunY = sun.y.toFixed(4);
      data.moonX = moon.x.toFixed(4);
      data.moonY = moon.y.toFixed(4);
      data.skyBody = isNight ? 'moon' : 'sun';
      data.skyBodyRadius = bodyRadius.toFixed(2);
      data.skyBodyVisible = visibility.toFixed(3);
      data.skyTime = time.toFixed(3);
      data.skyMoonRotation = this.moonRotation().toFixed(3);
      data.skyRayRotation = (time * .027 % TAU).toFixed(4);
      data.skyFrames = String(++this.frames);
      this.pendingStill = false;
    }

    drawFallbackGrade() {
      const ctx = this.ctx, data = this.scene.dataset;
      const fallback = data.renderer === 'fallback';
      const warm = fallback ? this.warm : 0, twilight = fallback ? this.twilight : 0;
      data.skyFallbackWarm = warm.toFixed(4); data.skyFallbackTwilight = twilight.toFixed(4);
      data.skyFallbackGrade = fallback ? warm + twilight > .002 ? 'sky-only' : 'neutral' : 'off';
      if (!fallback || warm + twilight < .002) return;
      // This is inside the exact celestial ridge clip, before stars and bodies.
      // Translucent source-over pigment retains the photo's clouds and detail;
      // no paint can reach the island, sea or sand below the silhouette.
      ctx.save(); ctx.globalCompositeOperation = 'source-over';
      const bottom = SOURCE_H * .52;
      if (warm > .001) {
        const sunset = this.phase >= .25;
        const glow = ctx.createLinearGradient(0, 0, 0, bottom);
        glow.addColorStop(0, `rgba(245,163,137,${warm * .045})`);
        glow.addColorStop(.32, `rgba(246,142,140,${warm * .13})`);
        glow.addColorStop(.65, sunset ? `rgba(255,120,133,${warm * .28})` : `rgba(255,192,105,${warm * .24})`);
        glow.addColorStop(.85, sunset ? `rgba(255,155,97,${warm * .34})` : `rgba(255,199,107,${warm * .32})`);
        glow.addColorStop(1, `rgba(255,183,118,${warm * .25})`);
        ctx.fillStyle = glow; ctx.fillRect(0, 0, SOURCE_W, bottom);
      }
      if (twilight > .001) {
        const violet = ctx.createLinearGradient(0, 0, 0, bottom);
        violet.addColorStop(0, `rgba(80,53,153,${twilight * .16})`);
        violet.addColorStop(.40, `rgba(118,60,190,${twilight * .26})`);
        violet.addColorStop(.74, `rgba(187,84,182,${twilight * .28})`);
        violet.addColorStop(1, `rgba(224,129,166,${twilight * .16})`);
        ctx.fillStyle = violet; ctx.fillRect(0, 0, SOURCE_W, bottom);
      }
      ctx.restore();
    }

    drawStars(opacity, time) {
      if (opacity < .005) return;
      const ctx = this.ctx;
      for (const star of this.stars) {
        const flicker = .7 + .3 * Math.sin(time * star.speed + star.phase);
        ctx.fillStyle = `rgba(215,230,250,${opacity * flicker * .74})`;
        ctx.beginPath();
        ctx.arc(star.x * SOURCE_W, star.y * SOURCE_H, star.radius, 0, TAU);
        ctx.fill();
        if (star.radius > .98) {
          ctx.strokeStyle = `rgba(205,225,255,${opacity * flicker * .12})`;
          ctx.lineWidth = .55;
          ctx.beginPath();
          ctx.moveTo(star.x * SOURCE_W - 3, star.y * SOURCE_H);
          ctx.lineTo(star.x * SOURCE_W + 3, star.y * SOURCE_H);
          ctx.moveTo(star.x * SOURCE_W, star.y * SOURCE_H - 3);
          ctx.lineTo(star.x * SOURCE_W, star.y * SOURCE_H + 3);
          ctx.stroke();
        }
      }
    }

    drawSun(position, visible) {
      const ctx = this.ctx;
      const x = position.x * SOURCE_W, y = position.y * SOURCE_H;
      const low = smooth(.22, .5, position.y);
      // Atmospheric scattering remains above the ridge as the disc sets.
      const atmospheric = ctx.createRadialGradient(x, y, 5, x, y, mix(125, 245, low));
      atmospheric.addColorStop(0, `rgba(255,231,155,${mix(.25, .37, low)})`);
      atmospheric.addColorStop(.18, `rgba(255,208,120,${mix(.08, .18, low)})`);
      atmospheric.addColorStop(.58, `rgba(255,165,78,${.065 * low})`);
      atmospheric.addColorStop(1, 'rgba(255,170,84,0)');
      ctx.fillStyle = atmospheric;
      ctx.fillRect(x - 250, y - 250, 500, 500);
      if (visible <= .002) return;
      const radius = mix(SUN_MIN_RADIUS, SUN_MAX_RADIUS, low);
      ctx.save();
      ctx.globalAlpha = visible;
      const rayVisibility = smooth(-.30, .85, (skyline(position.x) - position.y) * SOURCE_H / radius);
      if (rayVisibility > .002) this.drawSunRays(x, y, radius, rayVisibility);
      const haloRadius = radius * 3.15;
      const halo = ctx.createRadialGradient(x, y, radius * .72, x, y, haloRadius);
      halo.addColorStop(0, 'rgba(255,253,225,.68)');
      halo.addColorStop(.18, 'rgba(255,243,192,.24)');
      halo.addColorStop(.48, 'rgba(255,218,135,.055)');
      halo.addColorStop(1, 'rgba(255,212,124,0)');
      ctx.fillStyle = halo;
      ctx.fillRect(x - haloRadius, y - haloRadius, haloRadius * 2, haloRadius * 2);
      const disc = ctx.createRadialGradient(x - radius * .16, y - radius * .20, 0, x, y, radius);
      disc.addColorStop(0, '#fffef0');
      disc.addColorStop(.77, low > .5 ? '#fff4c6' : '#fffff3');
      disc.addColorStop(1, low > .5 ? '#ffd98a' : '#fff7d8');
      ctx.fillStyle = disc;
      ctx.beginPath(); ctx.arc(x, y, radius, 0, TAU); ctx.fill();
      if (low < .85) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(-.23);
        const beam = ctx.createLinearGradient(-80, 0, 80, 0);
        beam.addColorStop(0, 'rgba(255,240,197,0)');
        beam.addColorStop(.5, `rgba(255,251,232,${.2 * visible})`);
        beam.addColorStop(1, 'rgba(255,240,197,0)');
        ctx.fillStyle = beam;
        ctx.fillRect(-80, -.4, 160, .8);
        ctx.restore();
      }
      ctx.restore();
    }

    drawSunRays(x, y, radius, visibility) {
      const ctx = this.ctx;
      ctx.save(); ctx.translate(x, y); ctx.rotate(this.elapsed * .027);
      ctx.globalAlpha *= visibility;
      for (let i = 0; i < 12; i++) {
        ctx.save(); ctx.rotate(i * TAU / 12);
        const breath = 1 + .045 * Math.sin(this.elapsed * .80 + i * .52);
        const inner = radius * 1.04;
        const outer = radius * (i % 2 ? 1.70 : 2.04) * breath;
        const halfWidth = radius * (i % 2 ? .067 : .087);
        const ray = ctx.createLinearGradient(inner, 0, outer, 0);
        ray.addColorStop(0, 'rgba(255,203,74,.84)');
        ray.addColorStop(.24, 'rgba(255,212,98,.64)');
        ray.addColorStop(.72, 'rgba(255,219,131,.25)');
        ray.addColorStop(1, 'rgba(255,229,164,0)');
        ctx.fillStyle = ray;
        ctx.beginPath(); ctx.moveTo(inner, -halfWidth);
        ctx.quadraticCurveTo(inner + (outer - inner) * .68, -halfWidth * .45, outer, 0);
        ctx.quadraticCurveTo(inner + (outer - inner) * .68, halfWidth * .45, inner, halfWidth);
        ctx.closePath(); ctx.fill(); ctx.restore();
      }
      ctx.restore();
    }

    // Gentle libration uses the local motion clock, so neither phase wrap nor
    // selecting a still jumps the disc angle. Pausing/reduced motion freezes it.
    moonRotation() { return 8 * Math.sin(this.elapsed * .06); }

    drawMoon(position, visible) {
      if (visible < .002) return;
      const ctx = this.ctx, x = position.x * SOURCE_W, y = position.y * SOURCE_H;
      const radius = MOON_RADIUS;
      const haloRadius = radius * 4.6;
      ctx.save();
      ctx.globalAlpha = visible;
      const halo = ctx.createRadialGradient(x, y, radius * .9, x, y, haloRadius);
      halo.addColorStop(0, 'rgba(224,237,252,.35)');
      halo.addColorStop(.12, 'rgba(196,220,255,.15)');
      halo.addColorStop(.35, 'rgba(157,194,244,.048)');
      halo.addColorStop(.70, 'rgba(141,182,232,.008)');
      halo.addColorStop(1, 'rgba(141,187,228,0)');
      ctx.fillStyle = halo;
      ctx.fillRect(x - haloRadius, y - haloRadius, haloRadius * 2, haloRadius * 2);
      ctx.translate(x, y); ctx.rotate(this.moonRotation() * Math.PI / 180);
      ctx.drawImage(this.moon, -radius, -radius, radius * 2, radius * 2);
      ctx.restore();
    }

    drawClouds(time, night) {
      const ctx = this.ctx;
      ctx.save();
      ctx.globalAlpha = mix(.53, .08, night);
      for (let i = 0; i < 4; i++) {
        const travel = ((time * (1.7 + i * .22) + i * 439) % 2500) - 670;
        const height = 75 + i * 52;
        ctx.drawImage(this.cloud, travel, height, 730 + i * 73, 145);
      }
      ctx.restore();
    }

    drawMeteor(night, phase) {
      // One small meteor in the automated night, never repeated in a manual
      // still and never forced while reduced motion is active.
      if (this.mode !== 'cycle' || !this.canAnimate() || night < .7) return;
      const progress = (phase - .811) / .009;
      if (progress < 0 || progress > 1) return;
      const ctx = this.ctx, x = mix(.28, .43, progress) * SOURCE_W;
      const y = mix(.10, .23, progress) * SOURCE_H;
      const alpha = Math.sin(Math.PI * progress) * .66;
      const tail = ctx.createLinearGradient(x - 92, y - 45, x, y);
      tail.addColorStop(0, 'rgba(217,233,255,0)');
      tail.addColorStop(1, `rgba(231,244,255,${alpha})`);
      ctx.strokeStyle = tail;
      ctx.lineWidth = 1.05;
      ctx.beginPath(); ctx.moveTo(x - 92, y - 45); ctx.lineTo(x, y); ctx.stroke();
      ctx.fillStyle = `rgba(247,252,255,${alpha})`;
      ctx.beginPath(); ctx.arc(x, y, 1.2, 0, TAU); ctx.fill();
    }

    drawReflection(body, night, visibility, time) {
      if (visibility < .005) return;
      const ctx = this.ctx, x = body.x * SOURCE_W;
      const strength = visibility * (night ? .49 : .58);
      const tint = night ? '201,224,245' : '255,237,181';
      ctx.save();
      ctx.beginPath(); ctx.rect(0, SOURCE_H * .52, SOURCE_W, SOURCE_H * .26); ctx.clip();
      ctx.globalCompositeOperation = 'screen';
      for (const glint of this.glints) {
        const depth = glint.depth;
        const y = (.52 + depth * .26) * SOURCE_H;
        const perspective = .13 + depth * depth;
        const trailWidth = 12 + depth * depth * (night ? 118 : 160);
        const wave = Math.sin(time * glint.frequency + glint.phase);
        const shimmer = Math.pow(.5 + .5 * wave, 3);
        const falloff = Math.exp(-glint.spread * glint.spread * 2.3);
        const alpha = strength * shimmer * falloff * (1 - smooth(.83, 1, depth));
        if (alpha < .012) continue;
        const drift = Math.sin(time * .43 + depth * 33) * (1.5 + depth * 4);
        const length = (2 + glint.width * 17) * perspective;
        const glintX = x + glint.spread * trailWidth + drift;
        ctx.fillStyle = `rgba(${tint},${alpha.toFixed(3)})`;
        ctx.fillRect(glintX - length / 2, y + wave * perspective * 1.4, length, .45 + perspective * .66);
        if (shimmer > .94 && Math.abs(glint.spread) < .25 && depth > .4) {
          ctx.fillStyle = `rgba(255,250,230,${alpha * .14})`;
          ctx.fillRect(glintX - .45, y - 1.8 * perspective, .8, 4 * perspective);
        }
      }
      ctx.restore();
    }

    suspend() { this.suspended = true; this.updateMotion(); }
    resume() { this.suspended = false; this.pendingStill = true; this.resize(); this.updateMotion(); }
    destroy() {
      if (this.destroyed) return;
      this.stop();
      this.destroyed = true;
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.listeners.forEach(remove => remove());
      this.canvas.remove();
    }
  }

  // Theme editor section replacement must release all observers and frame
  // callbacks belonging to the old node before initialising its replacement.
  const registry = new Map();
  const scan = (root = document) => {
    const scenes = [...root.querySelectorAll('.coast-scene')];
    if (root.matches?.('.coast-scene')) scenes.unshift(root);
    for (const scene of scenes) {
      if (registry.has(scene) || scene.querySelector('[data-coast-sky]')) continue;
      const sky = new CoastalSky(scene);
      if (sky.ctx) registry.set(scene, sky);
    }
  };
  window.__cgauCoastalSkyRuntime = {version: '2026-09-07.3', scan};
  const start = () => {
    scan();
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event => {
      for (const [scene, sky] of registry) {
        if (event.target === scene || event.target.contains(scene)) {
          sky.destroy();
          registry.delete(scene);
        }
      }
    });
    window.addEventListener('pagehide', event => {
      registry.forEach(sky => event.persisted ? sky.suspend() : sky.destroy());
      if (!event.persisted) registry.clear();
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        registry.forEach(sky => sky.resume());
        scan();
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
